import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  DatePicker,
  Select,
  Typography,
  Space,
  Tag,
  Drawer,
  Divider,
  Button,
  Form,
  List,
  Avatar,
  Image,
  Alert,
  Row,
  Col,
  Upload,
  message,
  Statistic,
  Card,
} from "antd";

import dayjs from "dayjs";
import axios from "axios";
import constants from "../../constants";
import { EditOutlined, UploadOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import RegisterPayment from "./RegisterPayment";
import UploadDocument from "./UploadDocument";
import { useAuth } from "../../contexts/authContext";
import AddNewLead from "./AddNewLead";

// TODO: fetch the counsellor id through token and update the api in backend to fetch counsellor id from token

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const tailLayout = {
  wrapperCol: { offset: 0, span: 24 },
};

const LeadsInternational = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drawerData, setDrawerData] = useState(false);

  const [form] = Form.useForm();

  const [filters, setFilters] = useState({
    name: "",
    phone: "",
    school: "",
    followupDate: "",
    paymentStatus: "",
    documentStatus: "",
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Debounce only the inputs; selects/date will also be debounced uniformly for simplicity
  const debouncedFilters = {
    name: useDebouncedValue(filters.name, 400),
    phone: useDebouncedValue(filters.phone, 400),
    school: useDebouncedValue(filters.school, 400),
    followupDate: useDebouncedValue(filters.followupDate, 400),
    paymentStatus: useDebouncedValue(filters.paymentStatus, 400),
    documentStatus: useDebouncedValue(filters.documentStatus, 400),
  };

  const query = useMemo(
    () => ({ ...debouncedFilters, page, limit }),
    [
      debouncedFilters.name,
      debouncedFilters.phone,
      debouncedFilters.school,
      debouncedFilters.followupDate,
      debouncedFilters.paymentStatus,
      debouncedFilters.documentStatus,
      page,
      limit,
    ]
  );

  const { getToken } = useAuth();

  const fetchLeadsFromServer = async (query) => {
    const querySanitized = [`page=${query.page}`, `limit=${query.limit}`];
    Object.keys(query).forEach((key) => {
      if (query[key]) {
        if (key === "name" || key === "school" || key === "phone") {
          querySanitized.push(`${key}:cnts=${query[key]}`);
        } else if (key === "paymentStatus") {
          switch (query[key]) {
            case "Paid":
              querySanitized.push(`feeDue:eq=${0}`);
              break;
            case "Partial":
              querySanitized.push(`feeDue:gt=${0}`);
              querySanitized.push(`feePaid:gt=${0}`);
              break;
            case "Unpaid":
              querySanitized.push(`feePaid:eq=${0}`);
              break;
          }
        } else if (key === "documentStatus") {
          switch (query[key]) {
            case "Verified":
              querySanitized.push(`documentsCompleted:eq=${true}`);
              break;
            case "Submitted":
              querySanitized.push(`documentRecords:exists=${true}`);
              break;
            case "Pending":
              querySanitized.push(`documentRecords:exists=${false}`);
              break;
          }
        } else if (key === "followupDate") {
          let sd = new Date(query[key]);
          sd.setHours(0, 0, 0, 0);
          let ed = new Date(query[key]);
          ed.setDate(ed.getDate() + 1);
          ed.setHours(0, 0, 0, 0);

          let sdISO = sd.toISOString();
          let edISO = ed.toISOString();

          querySanitized.push(`followupDate:gte=${sdISO}&followupDate:lt=${edISO}`);
          // querySanitized.push(`followupDate=${new Date(query[key]).toDateString()}`);
        }
      }
    });

    try {
      const token = await getToken();
      const headers = {
        authorization: `bearer ${token}`,
      };

      const url = `${constants.NEW_DOMAIN}/api/v1/channelPartnerLeads/leads?${querySanitized.join("&")}`;
      const res = await axios.get(url, {
        headers,
      });
      //calculate size for pagination
      const len = res.data?.data?.length || 0;
      const size = len === query.limit ? query.limit * query.page + 1 : len;

      return {
        data: res.data?.data,
        total: size,
        page: query.page,
        limit: query.limit,
      };
    } catch (error) {
      throw new Error(error?.message || "something went wrong , try again later");
    }
  };

  const updateRowAfterUpload = (id, key, records) => {
    rows.map((data) => {
      if (data._id === id) {
        setDrawerData({ ...data, [key]: records });
      }
    });

    setRows((prev) => {
      return prev.map((data) => {
        if (data._id === id) {
          return { ...data, [key]: records };
        } else {
          return data;
        }
      });
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    fetchLeadsFromServer(query)
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Failed to load leads");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const updateDetails = async (form) => {
    if (!form.id) {
      setError("Student Id is missing");
    }
    setLoading(true);
    try {
      const token = await getToken();
      const headers = {
        authorization: `bearer ${token}`,
      };
      const url = `${constants.NEW_DOMAIN}/api/v1/channelPartnerLeads/updateLead/${form.id}`;
      const res = await axios.put(url, { ...form }, { headers });

      //update the record in table
      setRows((prev) => {
        const updatedRows = prev.map((data) => {
          if (data._id === form.id) {
            data = { ...form };
          }
          return data;
        });
        return updatedRows;
      });
      message.success("Data updated successfully");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.message || "something went wrong , try again later");
    }
  };

  const handleInputChange = (key) => (e) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const paymentOptions = ["", "Paid", "Unpaid", "Partial"];
  const documentOptions = ["", "Pending", "Submitted", "Verified"];

  const columns = [
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Name</span>
          <Input placeholder="Filter name" value={filters.name} onChange={handleInputChange("name")} allowClear size="small" />
        </Space>
      ),
      dataIndex: "name",
      key: "name",
      render: (text) => <Typography.Text>{text}</Typography.Text>,
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Phone</span>
          <Input placeholder="Filter phone" value={filters.phone} onChange={handleInputChange("phone")} allowClear size="small" />
        </Space>
      ),
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>School</span>
          <Input placeholder="Filter school" value={filters.school} onChange={handleInputChange("school")} allowClear size="small" />
        </Space>
      ),
      dataIndex: "school",
      key: "school",
      ellipsis: true,
      render: (school) => <small>{school}</small>,
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Follow Up Date</span>
          <DatePicker
            value={filters.followupDate ? dayjs(filters.followupDate) : null}
            onChange={(d) => handleInputChange("followupDate")({ target: { value: d ? d.format("YYYY-MM-DD") : "" } })}
            allowClear
            size="small"
            style={{ width: "100%" }}
          />
        </Space>
      ),
      dataIndex: "followupDate",
      key: "followupDate",
      render: (text) => (text ? dayjs(text).format("DD-MMM-YYYY") : "----------"),
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Payment Status</span>
          <Select
            value={filters.paymentStatus}
            onChange={(v) => handleInputChange("paymentStatus")({ target: { value: v } })}
            options={paymentOptions.map((opt) => ({ label: opt || "All", value: opt }))}
            size="small"
            style={{ width: 100 }}
            allowClear
          />
        </Space>
      ),

      key: "_id",
      render: (_, record) => {
        if (record.feePaid && record.feeDue) {
          return <Tag color="gold">Partial</Tag>;
        }
        if (record.feeDue === 0) {
          return <Tag color="green">Paid</Tag>;
        }
        if (record.feePaid === 0) {
          return <Tag color="red">Unpaid</Tag>;
        }
      },
    },
    {
      title: (
        <Space direction="vertical" size={4}>
          <span>Document Status</span>
          <Select
            value={filters.documentStatus}
            onChange={(v) => handleInputChange("documentStatus")({ target: { value: v } })}
            options={documentOptions.map((opt) => ({ label: opt || "All", value: opt }))}
            size="small"
            style={{ width: 100 }}
            allowClear
          />
        </Space>
      ),
      key: "_id",
      render: (_, record) => {
        if (record.documentsCompleted) {
          return <Tag color="green">Verified</Tag>;
        }
        if (Array.isArray(record.documentRecords) && record.documentRecords.length > 0) {
          return <Tag color="blue">Submitted</Tag>;
        }
        return <Tag color="red">Pending</Tag>;
      },
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <a
          onClick={() => {
            // form.setFieldsValue({ ...record, followupDate: record.followupDate ? dayjs(record.followupDate) : null, id: record._id });
            setDrawerData(record);
          }}
        >
          action <EditOutlined />
        </a>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    const { current, limit: newSize } = pagination;
    if (newSize !== limit) {
      setLimit(newSize);
    }
    setPage(current || 1);
  };

  return (
    <div style={{ padding: "12px" }}>
      <AddNewLead
        updateRow={(record) => {
          setRows((prev) => [record, ...prev]);
        }}
      />
      {error ? <Alert style={{ margin: "7px 0" }} message="Error" description={error} type="error" showIcon closable /> : null}

      <Drawer
        title="Student Details"
        closable={{ "aria-label": "Close Button" }}
        size="large"
        onClose={() => {
          setDrawerData(false);
          form.resetFields();
        }}
        destroyOnClose
        afterOpenChange={(visible) => {
          if (visible) {
            form.setFieldsValue({
              ...drawerData,
              followupDate: drawerData.followupDate ? dayjs(drawerData.followupDate) : null,
              id: drawerData._id,
            });
          } else {
            form.resetFields();
          }
        }}
        open={drawerData}
      >
        <Form form={form} size="small" variant="filled" layout="horizontal" name="control-hooks" onFinish={updateDetails}>
          <Divider orientation="middle">Personal</Divider>
          <Form.Item name="id" label="id" hidden rules={[{ required: true }]}>
            <Input disabled />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="school" label="School">
            <Input />
          </Form.Item>
          <Divider orientation="middle">Utility</Divider>
          <Form.Item name="followupDate" label="Next followup on">
            <DatePicker placement="topLeft" format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="comments" label="Comments">
            <TextArea />
          </Form.Item>

          {error && <Alert style={{ margin: "7px 0" }} message="Error" description={error} type="error" showIcon closable />}
          <Form.Item {...tailLayout}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                <EditOutlined /> Update
              </Button>
            </Space>
          </Form.Item>
        </Form>
        {/* payment records */}
        <Divider orientation="middle">Payment Records</Divider>
        <Row gutter={16} style={{ marginBottom: "10px" }}>
          <Col span={8}>
            <Card variant="borderless">
              <Statistic title="Total Fee" value={drawerData.totalFee} precision={2} valueStyle={{ color: "#3f8600" }} prefix="₹" />
            </Card>
          </Col>
          <Col span={8}>
            <Card variant="borderless">
              <Statistic title="Fee Paid (verified)" value={drawerData.feePaid} precision={2} valueStyle={{ color: "#971eff" }} prefix="₹" />
            </Card>
          </Col>
          <Col span={8}>
            <Card variant="borderless">
              <Statistic title="Fee Due" value={drawerData.feeDue} precision={2} valueStyle={{ color: "red" }} prefix="₹" />
            </Card>
          </Col>
        </Row>
        <RegisterPayment defaultList={drawerData.paymentRecords} updateRow={updateRowAfterUpload} leadId={drawerData._id} />

        {/* document records */}
        <UploadDocument defaultList={drawerData.documentRecords} updateRow={updateRowAfterUpload} leadId={drawerData._id} />
      </Drawer>
      <Table
        rowKey="_id"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ current: page, limit, total, showSizeChanger: true }}
        onChange={handleTableChange}
        scroll={{ x: true }}
      />
    </div>
  );
};

export default LeadsInternational;
